import { ClockIcon } from '@heroicons/react/20/solid';
import { twMerge } from 'tailwind-merge';

import formatReleaseLine from '@/renderer/utils/formatReleaseLine';

// Full am/pm game-time in the viewer's own timezone, e.g. "Sun 10:00am PT".
// `shortGeneric` yields the friendly zone abbreviation ("PT", not "PST"); the
// am/pm is lowercased and de-spaced to match the compact chip treatment.
function formatGameTime(iso?: string): { short: string; full: string } {
  if (!iso) {
    return { short: '', full: '' };
  }
  // Production stores gameTime as "YYYY-MM-DD HH:MM:SS+00" (CreateRelease). V8
  // rejects both the space separator and the hours-only "+00" offset as Invalid
  // Date, so normalize to a "T" separator and a full "+HH:00" offset first.
  const normalized = (iso.includes('T') ? iso : iso.replace(' ', 'T')).replace(
    /([+-]\d{2})$/,
    '$1:00',
  );
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) {
    return { short: '', full: '' };
  }
  const short = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'shortGeneric',
  })
    .format(d)
    .replace(',', '')
    .replace(/\s(AM|PM)\b/i, (_m, p) => p.toLowerCase());
  const full = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).format(d);
  return { short, full };
}

// Signed, two-decimal units result, e.g. "+1.00u" / "−1.08u" (real minus sign).
function formatUnitsResult(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toFixed(2)}u`;
}

const Release = ({ release }: { release: API.Release }) => {
  const { result, rating, unit, period, sport, gameTime, homeScore, visitorScore } = release;

  // formatReleaseLine is shared across the app (results, copy-plays, etc.) and
  // emits the compact "1H" / "2H" period token inline. On the release card we
  // spell it out ("1st half" / "2nd half") without changing the shared helper.
  let playLine = formatReleaseLine(release, false, true);
  if (period === 1) {
    playLine = playLine.replace(/\b1H\b/, '1st half');
  } else if (period === 2) {
    playLine = playLine.replace(/\b2H\b/, '2nd half');
  }

  const isPending = result === 'pending';
  const isGraded = result === 'win' || result === 'loss' || result === 'push';

  // Pill palette. The status color lives on the pill (and its dot) only — no
  // colored left rail, so the outcome isn't double-signaled on the card edge.
  const pillClass =
    result === 'win'
      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
      : result === 'loss'
        ? 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300'
        : result === 'pending'
          ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
          : 'bg-zinc-100 text-zinc-600 dark:bg-white/10 dark:text-zinc-300';

  const dotClass =
    result === 'win'
      ? 'bg-emerald-500'
      : result === 'loss'
        ? 'bg-rose-500'
        : result === 'pending'
          ? 'bg-amber-500'
          : 'bg-zinc-400';

  const statusLabel =
    result === 'noAction' ? 'No Action' : result.charAt(0).toUpperCase() + result.slice(1);

  const { short: gameTimeShort, full: gameTimeFull } = formatGameTime(gameTime);

  // Final score: just the two numbers, high-low, no team names or explainer.
  const hasScores =
    Number.isFinite(homeScore) &&
    Number.isFinite(visitorScore) &&
    ((homeScore ?? 0) > 0 || (visitorScore ?? 0) > 0);
  const finalScore =
    isGraded && hasScores
      ? `Final ${Math.max(homeScore, visitorScore)}–${Math.min(homeScore, visitorScore)}`
      : '';

  const hasUnitsResult = isGraded && typeof unit === 'number' && Number.isFinite(unit);

  // Staked units on a pending play. The standard 1u stays quiet (plain gray, no
  // pill); a non-standard size (1.5u, 2u, ...) is highlighted so it stands out.
  const showStake = isPending && typeof rating === 'number' && rating > 0;
  const stakeStandard = rating === 1;

  return (
    <div className="my-2 grid w-full grid-cols-1 overflow-hidden rounded-xl border border-zinc-950/10 bg-white shadow-sm sm:grid-cols-[minmax(0,1fr)_9rem] dark:border-white/10 dark:bg-zinc-900/60">
      {/* Left / main: the play. */}
      <div className="flex min-w-0 flex-col gap-2 p-4">
        <div className="break-words font-bold text-[15px] text-zinc-900 leading-snug tracking-tight tabular-nums sm:text-base dark:text-zinc-100">
          {playLine}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {sport?.name && (
            <span className="inline-flex items-center rounded-md bg-zinc-100 px-2 py-0.5 font-mono font-semibold text-[10px] text-zinc-600 uppercase tracking-wide dark:bg-white/10 dark:text-zinc-300">
              {sport.shortName || sport.name}
            </span>
          )}
          {gameTimeShort && (
            <span
              title={gameTimeFull}
              className="inline-flex items-center gap-1 rounded-full border border-zinc-950/10 bg-zinc-50 px-2 py-0.5 text-xs text-zinc-600 dark:border-white/10 dark:bg-white/5 dark:text-zinc-400"
            >
              <ClockIcon className="size-3 shrink-0 text-zinc-400 dark:text-zinc-500" />
              {gameTimeShort}
            </span>
          )}
          {finalScore && (
            <span className="font-mono text-xs text-zinc-500 tabular-nums dark:text-zinc-400">
              {finalScore}
            </span>
          )}
        </div>
      </div>

      {/* Right / status: pill + graded stats. Stacks under the play on mobile. */}
      <div className="flex flex-row flex-wrap items-center gap-2 border-zinc-950/10 border-t p-4 sm:flex-col sm:items-end sm:justify-center sm:border-t-0 sm:border-l sm:text-right dark:border-white/10">
        <span
          className={twMerge(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-semibold text-xs',
            pillClass,
          )}
        >
          <span className={twMerge('size-1.5 shrink-0 rounded-full', dotClass)} />
          {statusLabel}
        </span>
        {showStake && (
          <span
            className={twMerge(
              'font-mono text-xs tabular-nums',
              stakeStandard
                ? 'text-zinc-500 dark:text-zinc-400'
                : 'rounded-md bg-emerald-500/10 px-2 py-0.5 font-semibold text-emerald-700 dark:text-emerald-300',
            )}
          >
            {rating}u
          </span>
        )}
        {hasUnitsResult && (
          <span className="font-mono font-medium text-xs text-zinc-500 tabular-nums dark:text-zinc-400">
            {formatUnitsResult(unit ?? 0)}
          </span>
        )}
      </div>
    </div>
  );
};

export default Release;
